import { useEffect, useRef, useState } from 'react'

/** The hero's whole thesis is "keep the balloon up." So: tap it and it rises,
 *  gravity pulls it back toward the floor line, and you keep tapping. Nothing
 *  is stored anywhere - it resets on reload. */

const FLOOR = 76        // px the balloon can fall before its string meets the line
const CEILING = -130    // px it can rise above rest
const GRAVITY = 250     // px/s^2 - low, because balloons are slow
const IMPULSE = -205    // px/s upward per tap
const MAX_FALL = 145    // px/s terminal velocity

export function BalloonDiagram() {
  const [taps, setTaps] = useState(0)
  const [playing, setPlaying] = useState(false)
  const [landed, setLanded] = useState(false)
  const [squash, setSquash] = useState(false)

  const pos = useRef<SVGGElement>(null)
  const shadow = useRef<SVGGElement>(null)
  const y = useRef(0)
  const vel = useRef(0)
  const raf = useRef<number | null>(null)

  const reduced = typeof window !== 'undefined'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches

  function paint(v: number) {
    if (pos.current) pos.current.style.transform = `translateY(${v}px)`
    if (shadow.current) {
      const t = (v - CEILING) / (FLOOR - CEILING)   // 0 high, 1 on the floor
      shadow.current.style.transform = `scaleX(${(0.55 + t * 0.5).toFixed(3)})`
      shadow.current.style.opacity = String((0.18 + t * 0.42).toFixed(3))
    }
  }

  function stop() {
    if (raf.current !== null) cancelAnimationFrame(raf.current)
    raf.current = null
  }

  function run() {
    stop()
    let last = performance.now()
    const step = (now: number) => {
      const dt = Math.min((now - last) / 1000, 0.05)
      last = now
      vel.current = Math.min(vel.current + GRAVITY * dt, MAX_FALL)
      y.current += vel.current * dt

      if (y.current <= CEILING) { y.current = CEILING; vel.current = 0 }

      if (y.current >= FLOOR) {
        y.current = FLOOR
        paint(y.current)
        stop()
        setPlaying(false)
        setLanded(true)
        return
      }
      paint(y.current)
      raf.current = requestAnimationFrame(step)
    }
    raf.current = requestAnimationFrame(step)
  }

  function tap() {
    setSquash(true)
    if (reduced) { setTaps((n) => n + 1); return }

    if (landed) {           // restart from wherever it came to rest
      setLanded(false)
      setTaps(1)
    } else {
      setTaps((n) => n + 1)
    }
    vel.current = IMPULSE
    setPlaying(true)
    run()
  }

  function onKey(e: React.KeyboardEvent) {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); tap() }
  }

  useEffect(() => stop, [])

  const status = landed ? `${taps} ${taps === 1 ? 'TAP' : 'TAPS'} · TAP TO PLAY AGAIN`
    : playing ? `${taps} ${taps === 1 ? 'TAP' : 'TAPS'} · DON'T LET IT LAND`
    : 'KEEP IT UP · TAP THE BALLOON'

  return (
    <svg className={`diagram${playing || landed ? ' playing' : ''}`} viewBox="0 0 420 336"
      role="img" aria-label="Tap the balloon to keep it from landing on the floor line.">
      <line className="floor" x1="20" y1="296" x2="400" y2="296" />
      <path className="arc" d="M40 264 Q 110 120 180 264 Q 250 120 320 264 Q 355 198 392 246" />
      <circle cx="40" cy="264" r="5" fill="var(--a3)" />
      <circle cx="180" cy="264" r="5" fill="var(--a3)" />
      <circle cx="320" cy="264" r="5" fill="var(--a3)" />
      <text className="dlabel" x="30" y="288">TAP</text>
      <text className="dlabel" x="170" y="288">TAP</text>
      <text className="dlabel" x="310" y="288">TAP</text>

      <g className="shad" ref={shadow}>
        <ellipse cx="180" cy="296" rx="34" ry="7" fill="var(--edge)" />
      </g>

      <g className="float">
        <g ref={pos}>
          <g
            className={`balloon-hit${squash ? ' squash' : ''}`}
            onAnimationEnd={() => setSquash(false)}
            onClick={tap}
            onKeyDown={onKey}
            role="button"
            tabIndex={0}
            aria-label={`Bounce the balloon. Tapped ${taps} times.`}
          >
            <ellipse className="focus-ring" cx="180" cy="116" rx="57" ry="65" />
            <path d="M180 174 q 8 16 -2 27 q -8 10 2 19" stroke="var(--a1)" strokeWidth="2" fill="none" opacity=".75" />
            <path d="M175 168 h10 l-5 10 z" fill="var(--a1)" />
            <ellipse cx="180" cy="116" rx="51" ry="59" fill="var(--a1)" />
            <ellipse cx="163" cy="94" rx="13" ry="19" fill="#fff" opacity=".33" transform="rotate(-20 163 94)" />
          </g>
        </g>
      </g>

      {landed && (
        <g className="cheer">
          <text className="cheer-big" x="210" y="84" textAnchor="middle">Good job!</text>
          <text className="cheer-sub" x="210" y="108" textAnchor="middle">
            {taps} {taps === 1 ? 'tap' : 'taps'}
          </text>
        </g>
      )}

      <text className="dlabel" x="210" y="30" textAnchor="middle" aria-live="polite">{status}</text>
    </svg>
  )
}
